import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '~/lib/cn'

/** Matches the leave animation, after which the afterimage is gone for good. */
const LEAVE_MS = 150

/**
 * The overlay every dialog is built on. It exists so that the three things a modal has to get
 * right are got right once: it covers the window, the page behind it does not move, and it is
 * reachable however tall it grows.
 *
 * Covering the window is the one that cannot be done where the dialog is written. `fixed` is
 * laid out against the nearest ancestor carrying a transform, a filter or a backdrop-filter,
 * and only against the window when there is none — so a dialog opened from the header, which
 * blurs what is behind it once the page has been scrolled, took the header's own box for the
 * window and came out as a strip inside it. Every dialog therefore goes to the body, past
 * whatever it was written inside of.
 *
 * The rest follows from the backdrop: without it the page scrolls on underneath, so what shows
 * through changes as you scroll, and a dialog taller than the screen has its far end somewhere
 * nobody can reach.
 */

interface OpenDialog {
  element: HTMLElement
  /** Read through a ref, so re-rendering a dialog never re-registers it. */
  dismiss: { current: () => void }
}

/** The dialogs currently open. The page stays locked until the last of them has gone. */
const open: OpenDialog[] = []

/**
 * The one Escape belongs to: the last in the document. Registration order cannot be trusted
 * for this — React runs a child's effect before its re-running parent's, so a dialog that
 * re-renders while another is open would push itself back on top of it.
 */
function innermost(): OpenDialog | null {
  return open.reduce<OpenDialog | null>((deepest, entry) => {
    if (deepest === null) {
      return entry
    }
    const where = deepest.element.compareDocumentPosition(entry.element)
    return (where & Node.DOCUMENT_POSITION_FOLLOWING) === 0 ? deepest : entry
  }, null)
}

function onKeyDown(event: KeyboardEvent): void {
  if (event.key !== 'Escape') {
    return
  }
  const inner = innermost()
  if (inner !== null) {
    event.stopPropagation()
    inner.dismiss.current()
  }
}

/**
 * Holds the page still. The scrollbar is never drawn here (see the web design spec), but the
 * width it would take is compensated anyway rather than assumed away — a browser told to show
 * one would shift the whole page sideways as a dialog opened.
 */
function lockPage(): () => void {
  const { body } = document
  const overflow = body.style.overflow
  const paddingRight = body.style.paddingRight
  const position = body.style.position
  const top = body.style.top
  const left = body.style.left
  const width = body.style.width
  const scrollX = window.scrollX
  const scrollY = window.scrollY
  const gap = window.innerWidth - document.documentElement.clientWidth

  body.style.overflow = 'hidden'
  body.style.position = 'fixed'
  body.style.top = `${String(-scrollY)}px`
  body.style.left = `${String(-scrollX)}px`
  body.style.width = '100%'
  if (gap > 0) {
    body.style.paddingRight = `${String(gap)}px`
  }

  return () => {
    body.style.overflow = overflow
    body.style.paddingRight = paddingRight
    body.style.position = position
    body.style.top = top
    body.style.left = left
    body.style.width = width
    window.scrollTo({ left: scrollX, top: scrollY, behavior: 'instant' })
  }
}

let unlock: (() => void) | null = null

export function Dialog({
  label,
  onDismiss,
  className,
  origin,
  at,
  dismissOnBackdrop = true,
  children,
}: {
  label: string
  /**
   * Where on the window the dialog comes out of, and goes back into: a page opened from a
   * control grows out of that control rather than appearing in the middle of nowhere. Left
   * out, it arrives the ordinary way.
   */
  origin?: { x: number; y: number } | undefined
  /**
   * Asked to leave — Escape, for now. Where there is something to lose, raise the
   * confirmation from here rather than closing.
   */
  onDismiss: () => void
  /**
   * Where on the window the dialog stands, for one opened from a control rather than about the
   * page as a whole: it is laid under that point rather than in the middle of the window, and
   * flips above it where there is no room below. Left out, it stands in the middle.
   */
  at?: { x: number; y: number } | undefined
  /**
   * Whether a press on the backdrop puts it away. Off for a dialog worked beside, where a stray
   * press beside it is no answer — Escape and its own close still do.
   */
  dismissOnBackdrop?: boolean
  /** Merged over the panel's own classes, for a dialog that needs a different width. */
  className?: string | undefined
  children: ReactNode
}) {
  const backdrop = useRef<HTMLDivElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const dismiss = useRef(onDismiss)

  /*
   * Where the panel grows from, in its own coordinates: the point on the window it was opened
   * from, less where the panel itself has been laid. Worked out once the panel has a box, and
   * kept on it, so the way back in — which plays on the same node after it has left — turns
   * about the same point.
   */
  const originX = origin?.x
  const originY = origin?.y
  useLayoutEffect(() => {
    const element = panel.current
    if (element === null || originX === undefined || originY === undefined) {
      return
    }
    const box = element.getBoundingClientRect()
    element.style.transformOrigin = `${String(originX - box.left)}px ${String(originY - box.top)}px`
  }, [originX, originY])

  /*
   * Laid near the press that opened it, where it was opened from one: under the point, and
   * above it where the window has no room under it, and never past the window's edges. It is
   * measured rather than guessed, and measured again as the panel changes size, since these
   * panels morph from one size to another while they stand.
   */
  const atX = at?.x
  const atY = at?.y
  const [spot, setSpot] = useState<{ left: number; top: number } | null>(null)
  useLayoutEffect(() => {
    const element = panel.current
    if (element === null || atX === undefined || atY === undefined) {
      return
    }
    const margin = 12
    const place = () => {
      const box = element.getBoundingClientRect()
      const room = window.innerHeight - atY - margin
      const top = box.height <= room ? atY + margin : Math.max(margin, atY - box.height - margin)
      setSpot({
        left: Math.min(
          Math.max(margin, atX - box.width / 2),
          Math.max(margin, window.innerWidth - box.width - margin),
        ),
        top: Math.min(top, Math.max(margin, window.innerHeight - box.height - margin)),
      })
    }
    place()
    const observer = new ResizeObserver(place)
    observer.observe(element)
    return () => {
      observer.disconnect()
    }
  }, [atX, atY])

  useEffect(() => {
    dismiss.current = onDismiss
  })

  useEffect(() => {
    const element = backdrop.current
    if (element === null) {
      return
    }

    const entry: OpenDialog = { element, dismiss }
    open.push(entry)

    if (open.length === 1) {
      document.addEventListener('keydown', onKeyDown)
      unlock = lockPage()
    }

    return () => {
      const at = open.indexOf(entry)
      if (at !== -1) {
        open.splice(at, 1)
      }
      if (open.length === 0) {
        document.removeEventListener('keydown', onKeyDown)
        unlock?.()
        unlock = null
      }
    }
  }, [])

  /*
   * The way out is animated here, at the one boundary every dialog crosses: unmounting.
   * Whatever closed it — Escape, the backdrop, a Cancel, a navigation — React has already
   * taken the node out by the time anything could ease it away, so the node itself is asked
   * to linger: put back, inert and hidden from the accessibility tree, for as long as the
   * leave animation runs, and gone for good after it.
   *
   * The microtask is what tells a real close from StrictMode's rehearsal, which runs this
   * cleanup with the dialog still mounted: once the commit has finished, only a node no
   * longer in the document was actually closed.
   */
  useLayoutEffect(() => {
    const element = backdrop.current
    if (element === null) {
      return
    }
    return () => {
      queueMicrotask(() => {
        if (element.isConnected) {
          return
        }
        if (
          typeof window.matchMedia === 'function' &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) {
          return
        }
        element.setAttribute('aria-hidden', 'true')
        element.setAttribute('inert', '')
        element.classList.add('dialog-leave')
        document.body.append(element)
        window.setTimeout(() => {
          element.remove()
        }, LEAVE_MS)
      })
    }
  }, [])

  return createPortal(
    <div
      ref={backdrop}
      role="dialog"
      aria-label={label}
      aria-modal="true"
      // The backdrop scrolls, not the page: a dialog taller than the viewport is reached by
      // scrolling the overlay, and `overscroll-contain` keeps that from reaching the page.
      className="dialog-in fixed inset-0 z-50 overflow-y-auto overscroll-contain bg-black/60"
    >
      {/* A real control, not a clickable div: it stays behind the panel and pinned to the
          viewport while a tall dialog scrolls, so every exposed part of the backdrop answers. */}
      <button
        type="button"
        aria-label={`Close ${label}`}
        className="fixed inset-0 cursor-default"
        tabIndex={dismissOnBackdrop ? undefined : -1}
        aria-hidden={dismissOnBackdrop ? undefined : true}
        onClick={() => {
          if (dismissOnBackdrop) dismiss.current()
        }}
      />
      {/* `min-h-full` with centred items keeps a short dialog in the middle and lets a tall one
          grow past the top, which `place-items-center` in a scroll container would put out of
          reach. */}
      <div
        className={cn(
          'pointer-events-none relative min-h-full p-6',
          at === undefined && 'flex items-center justify-center',
        )}
      >
        <div
          ref={panel}
          className={cn(
            'dialog-panel pointer-events-auto w-full max-w-sm space-y-4 rounded-3xl bg-surface-raised p-6',
            origin !== undefined && 'dialog-panel-from',
            at !== undefined && 'fixed',
            className,
          )}
          style={
            at === undefined
              ? undefined
              : // Off the window's edge until it has been measured, rather than in the middle
                // for a frame and then somewhere else.
                { left: spot?.left ?? -9999, top: spot?.top ?? -9999 }
          }
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  )
}
